import * as React from "react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import type { FloatingAction, FloatingActionBarProps } from "./FloatingActionBar.types";
import * as s from "./FloatingActionBar.style.css";

export type { FloatingAction, FloatingActionBarProps, FloatingActionShape } from "./FloatingActionBar.types";

function resolveShape(action: FloatingAction): "rectangle" | "circle" {
  if (action.shape) return action.shape;
  return action.label ? "rectangle" : "circle";
}

function FloatingActionButton({ action }: { action: FloatingAction }) {
  const [hovered, setHovered] = React.useState(false);
  const shape = resolveShape(action);
  const label = action.loading ? "Saving…" : action.label;

  if (shape === "circle") {
    return (
      <button
        type="button"
        className={s.circleBtn}
        disabled={action.disabled || action.loading}
        title={typeof action.tooltip === "string" ? action.tooltip : action.label}
        aria-label={action.label ?? action.id}
        onClick={action.onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {hovered && action.tooltip ? <div className={s.tooltip}>{action.tooltip}</div> : null}
        {action.icon}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={clsx(
        s.rectBtn,
        action.color === "neutral" && s.rectBtnNeutral,
        action.color === "danger" && s.rectBtnDanger,
      )}
      disabled={action.disabled || action.loading}
      onClick={action.onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && action.tooltip ? <div className={s.tooltip}>{action.tooltip}</div> : null}
      {action.icon}
      {label}
    </button>
  );
}

export function FloatingActionBar({ actions, className }: FloatingActionBarProps) {
  const barRef = React.useRef<HTMLDivElement>(null);
  const prevCountRef = React.useRef(actions.length);

  // Blur focused action buttons before they leave the tree so the browser
  // does not reset the page scroll container when Save disappears.
  React.useLayoutEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = actions.length;
    if (prev === 0 || actions.length > 0) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && barRef.current?.contains(active)) {
      active.blur();
    }
  }, [actions.length]);

  return (
    <div
      ref={barRef}
      className={clsx(s.bar, className)}
      aria-live="polite"
      data-floating-action-bar=""
    >
      <AnimatePresence initial={false}>
        {actions.map((action) => (
          <motion.div
            key={action.id}
            className={s.actionMotion}
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.92 }}
            transition={{ type: "spring", stiffness: 420, damping: 28 }}
          >
            <FloatingActionButton action={action} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
